import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useEffect } from 'react';
import { useGetClientCountMetricsMutation } from 'services/api/relifDashboard.api';
import { formatSpanishMonthForDisplay } from 'utils/dateHelpers';

import useClientsData from '../hooks/useClientsData';
import IndustryLineChart from './charts/IndustryLineChart';
import ErrorState from './common/ErrorState';
import LoadingState from './common/LoadingState';
import StatCard from './StatCard';

interface ClientsDashboardProps {
  selectedYear: number;
}

const ClientsDashboard: React.FC<ClientsDashboardProps> = ({ selectedYear }) => {
  const [getClientCountMetrics, { data: clientData, isLoading }] = useGetClientCountMetricsMutation();

  useEffect(() => {
    getClientCountMetrics({ year: selectedYear }).catch(() => {});
  }, [selectedYear, getClientCountMetrics]);

  const { clientCountByIndustryData, totalLineData, monthLabels, monthOverMonthGrowth, lastAvailableMonth } =
    useClientsData(clientData, selectedYear);

  if (isLoading) {
    return <LoadingState />;
  }

  if (clientData?.error) {
    return (
      <ErrorState
        title="Error al cargar datos de clientes"
        message={clientData.error}
        additionalInfo="Intente de nuevo más tarde."
      />
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Número de Clientes al Cierre de Cada Mes
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Anual"
            value={clientData?.totals?.yearlyClientCount || 0}
            subtitle={`Año ${selectedYear}`}
            color="primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Nuevos Clientes"
            value={clientData?.totals?.latestMonthClientCount || 0}
            subtitle={lastAvailableMonth ? formatSpanishMonthForDisplay(lastAvailableMonth) : 'Último mes'}
            color="secondary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Crecimiento Mensual"
            value={monthOverMonthGrowth}
            subtitle="Comparación mes a mes"
            color={monthOverMonthGrowth >= 0 ? 'success' : 'warning'}
            isPercentage
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Clientes Conectados al Relifverse"
            value={0}
            subtitle="Porcentaje de clientes en Relifverse"
            color="info"
            isPercentage
          />
        </Grid>
      </Grid>
      {clientCountByIndustryData.length > 0 && monthLabels.length > 0 && (
        <Grid container spacing={3}>
          <Grid size={12}>
            <IndustryLineChart
              title={`Clientes por Industria - ${selectedYear}`}
              industryData={clientCountByIndustryData}
              totalLineData={totalLineData}
              monthLabels={monthLabels}
              yAxisLabel="Clientes"
            />
          </Grid>
        </Grid>
      )}
      {clientCountByIndustryData.length === 0 && !isLoading && (
        <Card sx={{ borderRadius: 3, p: 3 }}>
          <Typography
            variant="body1"
            align="center"
            sx={{
              color: 'text.secondary',
            }}
          >
            No hay datos de clientes disponibles para el año {selectedYear}
          </Typography>
        </Card>
      )}
    </Box>
  );
};

export default ClientsDashboard;
